/**
 * What a recursive change covers and what its confirmation protects (S8 Task 8.11, findings S8-07 / S8-R2-01 / S8-R2-08):
 * "Applies to …", the `aclRecursiveGuaranteeLines` sentences VERBATIM for the facts in force (since UI/UX fix round A the
 * checks behind a "How Jam SQL checks this change" disclosure, `propagateCopyParts`), the 100-failures line, the
 * default-scope effect, the recalculated-mask warning of a remove and of a merge that does not set a scope's mask (row
 * C7), the mask a merge writes onto every path (review opus F3), and the effect on the root itself ("Other paths may
 * differ."). A file-system root gets its own guarantee text.
 */
import { defaultScopeSentence, mergeMaskSentences, removeMaskSentence } from '../../../../../core/engines/azureStorageAclChangeText';
import { aclModifyMaskLine, aclRootIdentity } from '../../../../../core/engines/azureStorageAclJobText';
import { hnsServiceFacts } from '../../../../../core/engines/azureStorageHnsFacts';
import type { StorageAclChange, StorageHnsAccessControl } from '../../../../../types/azureStorage';
import { propagateCopyParts, rootResultOf } from '../aclPropagate';
import { trackStorageEvent } from '../../services/storageAnalytics';
import { AclChangePreview } from './AclChangePreview';

type Props = { root: string; shortEtag?: string; change: StorageAclChange; rootAcl: StorageHnsAccessControl };

export function PropagateCopy({ root, shortEtag, change, rootAcl }: Props) {
  const facts = hnsServiceFacts();
  const defaults = defaultScopeSentence(change, root, facts);
  const remove = removeMaskSentence(change, facts);
  const modifyMask = aclModifyMaskLine(change, facts);
  const mergeMask = mergeMaskSentences(change, root, facts);
  const { summary, checks } = propagateCopyParts(root, shortEtag, facts, aclRootIdentity(rootAcl.resource));
  return (
    <div className="grid gap-2 text-xs">
      {/* U28 / A17: the scope, "no undo" and the concurrent-change line up front; the checks behind a disclosure. */}
      <div className="grid gap-1 rounded border border-border p-2" data-testid="hns-propagate-copy">
        {summary.map((line) => <p key={line} className={line === summary[0] ? 'font-medium' : undefined}>{line}</p>)}
        <details data-testid="hns-propagate-checks" onToggle={(event) => { if ((event.currentTarget as HTMLDetailsElement).open) trackStorageEvent('feature:storage_hns_propagate_checks', { opened: true }); }}>
          <summary className="cursor-pointer text-muted-foreground">How Jam SQL checks this change</summary>
          <ul className="mt-1 grid list-disc gap-1 pl-4 text-muted-foreground">{checks.map((line) => <li key={line}>{line}</li>)}</ul>
        </details>
      </div>
      {defaults && <p data-testid="hns-propagate-defaults">{defaults}</p>}
      {remove && <p className="text-warning" data-testid="hns-propagate-remove-mask">{remove}</p>}
      {modifyMask && <p className="text-warning" data-testid="hns-propagate-modify-mask">{modifyMask}</p>}
      {mergeMask.map((line) => <p key={line} className="text-warning" data-testid="hns-propagate-merge-mask">{line}</p>)}
      <AclChangePreview before={{ entries: rootAcl.entries }} after={{ entries: rootResultOf(rootAcl.entries, change, facts) }} displayNames={rootAcl.displayNames}
        title={`Effect on ${root}`} note="Other paths may differ." testId="hns-propagate-preview" />
    </div>
  );
}
